import { CurrencyPipe, DatePipe } from '@angular/common';
import { Component, OnInit, Input, OnDestroy, EventEmitter, Output, AfterViewInit } from '@angular/core';
import { Router, NavigationStart } from '@angular/router';
import { Subscription, Observable, of, BehaviorSubject } from 'rxjs';
import { filter, switchMap } from 'rxjs/operators';
//import { GridOptions, ColDef } from '@ag-grid-enterprise/all-modules';
import { GridOptions, ColDef, GridApi } from 'ag-grid-community';

import { RouterLinkRendererComponent } from '../ag-grid/router-link-renderer.component';

import { GridFormatterService } from '../../../common/services/grid-formatter.service';
import { DynamicDialogService } from '../../../core/services/dynamic-dialog.service';
import { FinancialAssuranceService } from '../../services/financial-assurance.service';
import { LoaderService } from '../../services/loader.service';

import { RequiredCumulativeFinancialInstrument, FinancialInstrument, FinancialInstrumentType, FinancialAssuranceSummary } from '../../models/financialAssurance.model';
import { dateComparator } from '../data-table/comparators/data-table.comparators';
import { tap } from 'rxjs/operators';
import { AuthService } from '../../../services/auth.service';
import { LunaClaimTypes, LunaClaimValues } from '../../../models/user-identity.model';
import { OrganizationService } from "../../../common/services/organization.service";
import { OrganizationComplianceDetails } from "../../../common/models/organization.model";

@Component({
    selector: 'fa-bonding',
    templateUrl: './fa-bonding.component.html',
    standalone: false
})
export class FinancialAssuranceBondingComponent implements OnInit, OnDestroy, AfterViewInit {

    @Input() public externalView: boolean = true;
    @Input() public orgId: string;
    _isRecalculating: boolean = false;

    get isRecalculating(): boolean {
        return this._isRecalculating;
    }

    @Input() set isRecalculating(value: boolean) {
        this._isRecalculating = value;
    }

    _isLoading: boolean = true;

    get isLoading(): boolean {
        return this._isLoading;
    }

    @Input() set isLoading(value: boolean) {
        this._isLoading = value;
    }

    get activeBondingAmount(): string {
        if (this.isLoading) return "Loading...";
        if (this.isRecalculating) return "Calculating...";
        if (this.complianceDetails != null && this.complianceDetails.bondingProvided != null) {
            return this.currencyPipe.transform(this.complianceDetails.bondingProvided);
        }
        return "Unavailable";
    }
    get requiredBondingAmount(): string {
        if (this.isLoading) return "Loading...";
        if (this.isRecalculating) return "Calculating...";
        if (this.complianceDetails != null && this.complianceDetails.requiredBonding != null) {
            return this.currencyPipe.transform(this.complianceDetails.requiredBonding);
        }
        return "Unavailable";
    }
    public bondingInfo: string;

    private _complianceDetails: OrganizationComplianceDetails;

    get complianceDetails(): OrganizationComplianceDetails {
        return this._complianceDetails;
    }

    @Input() gridAriaLabel: string;
    @Input() set complianceDetails(summary: OrganizationComplianceDetails) {
        this._complianceDetails = summary;
        if (summary != null) {
            this.bondingInfo = this.composeInfo(summary);
        } else {
            this.bondingInfo = "";
        }

    }

    public colAmount: ColDef = {
        headerName: 'Amount',
        headerTooltip: 'Amount',
        tooltipValueGetter: this.formatterService.formatCurrency('amount'),
        field: 'amount',
        filter: "agNumberColumnFilter",
        valueFormatter: this.formatterService.formatCurrency('amount')
    };

    public colExpires: ColDef = {
        headerName: 'Expires',
        headerTooltip: 'Expires',
		tooltipValueGetter: this.formatterService.formatDate("expirationDate", "MM/dd/yyyy", "N/A", "+0000"),
        field: 'expirationDate',
		valueFormatter: this.formatterService.formatDate("expirationDate", "MM/dd/yyyy", "N/A", "+0000"),
        comparator: dateComparator,
        filter: "agDateColumnFilter",
        filterParams: this.formatterService.defaultDateFilterParams,
    }

    public colType: ColDef = {
        headerName: 'Type',
        headerTooltip: 'Type',
        tooltipValueGetter: this.assuranceType(),
        field: 'typeCode',
        valueFormatter: this.assuranceType()
    };

    public colGuarantorName: ColDef = {
        headerName: 'Guarantor Name',
        headerTooltip: 'Guarantor Name',
        tooltipValueGetter: (params) => this.guarantorNameFormatter(params.value),
        field: 'guarantor.name',
        valueFormatter: (params) => this.guarantorNameFormatter(params.value),
        filter: "agTextColumnFilter",
        filterParams: this.formatterService.defaultTextFilterParams,

    };

    public colInstrumentNumber: ColDef = {
        headerName: 'Instrument Number',
        headerTooltip: 'Financial Assurance Number (Instrument Number)',
        tooltipValueGetter: this.formatterService.formatPotentiallyBlankField("instrumentId", "N/A"),
        field: 'instrumentId',
        valueFormatter: this.formatterService.formatPotentiallyBlankField("instrumentId", "N/A"),
        filter: "agTextColumnFilter",
        filterParams: this.formatterService.defaultTextFilterParams,

    }


    @Output() recalculateEvent: EventEmitter<string> = new EventEmitter<string>();

    public typeDescriptions: any[] = [];

   
    public subscriptions: Subscription = new Subscription();
    public bonds: FinancialInstrument[];
    private _bonds: BehaviorSubject<FinancialInstrument[]> = new BehaviorSubject<FinancialInstrument[]>(null);


	public gridOptions: GridOptions;
	private gridApi?: GridApi;
    get isCompliant(): boolean {
        if (this.complianceDetails == null) return false;
        return this.complianceDetails.isBondingCompliant;
    }
    public orgName: string = '';

     public hasAddBondPermission: boolean = false;

	constructor(
		public financialAssuranceService: FinancialAssuranceService,
		public organizationService: OrganizationService,
		public currencyPipe: CurrencyPipe,
		public datePipe: DatePipe,
		public router: Router,
		private formatterService: GridFormatterService,
		public dialogService: DynamicDialogService,
		public loader: LoaderService,
		public authService: AuthService
	) { }

    ngOnInit() {
        this.loadData();
        this.gridOptions = this.formatterService.BuildDefaultGridOptionsWithColumns(this.externalView ? this.buildExternalColumns() : this.buildInternalColumns());
        this.gridOptions.getRowClass = (params: any) => {
            if (!this.isBondActive(params.data)) return 'red';
		};
		this.gridOptions.onGridReady = (params) => { 
            this.gridApi = params.api; 
            this.gridApi.sizeColumnsToFit();
        };
        this.hasAddBondPermission = this.authService.User.allClaims.filter(a => a.type == LunaClaimTypes.System && a.value == LunaClaimValues.CanAddBonding).length > 0;

        this.router.events.pipe(filter(e => e instanceof NavigationStart)).subscribe((routerEvent: NavigationStart) => {
            this.subscriptions.unsubscribe();
        });
    }

    ngAfterViewInit() {
		if (this.gridApi) this.gridApi.sizeColumnsToFit();
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }

    public addBond(): void {
        this.router.navigateByUrl('/flow/create/nvE7GRWnvEcdPo1Xic');
    }

    buildExternalColumns(): ColDef[] {
        return [
            this.colType,
            this.colAmount,
            this.colExpires,
            this.colInstrumentNumber,
            this.colGuarantorName,
        ] as ColDef[];
    }

    buildInternalColumns(): ColDef[] {
        return [
            {
                headerName: 'Type',
                headerTooltip: 'Type',
                tooltipValueGetter: this.assuranceType(),
                field: 'typeCode',
                cellRenderer: RouterLinkRendererComponent,
                cellRendererParams: {
                    textFunction: this.assuranceType(),
                    url: "/admin/bond",
                    idField: "id"
                },
            },
            this.colAmount,
            this.colExpires,
            this.colInstrumentNumber,
            this.colGuarantorName
            //,this.colDelete
        ] as ColDef[];
    }



    public canAddBond(): boolean {
        return this.hasAddBondPermission;

    }

    public loadData(): void {
        let type$: Observable<any> = this.financialAssuranceService.TypeDescriptions.pipe(
            tap((data) => {
                this.typeDescriptions = data;
            })
        );

        let summaryObs$: Observable<any> = this.financialAssuranceService.getBonds(this.orgId).pipe(
            tap((bonds: FinancialInstrument[]) => {
                let expiredBonds: FinancialInstrument[] = [];
                let activeBonds: FinancialInstrument[] = [];
                bonds.forEach(b => {
                    if (b.isActive) activeBonds.push(b);
                    else expiredBonds.push(b);
                });
                activeBonds = activeBonds.sort((a, b) => {
                    if (a.expirationDate < b.expirationDate) return 1;
                    if (a.expirationDate > b.expirationDate) return -1;
                    return 0;
                });
                expiredBonds = expiredBonds.sort((a, b) => {
                    if (a.expirationDate < b.expirationDate) return 1;
                    if (a.expirationDate > b.expirationDate) return -1;
                    return 0;
                });
                this.bonds = activeBonds.concat(expiredBonds);
            })
        );
        const summary$ = type$.pipe(
            switchMap(() => {
                return summaryObs$;
            })
        )
        .subscribe(
            () => {  },
            (error) => { console.error(error); }
        );
        this.subscriptions.add(summary$);
    }

    public composeInfo(summary: OrganizationComplianceDetails): string {
        let info = (summary.bondingDecisionBasis != null)
            ? summary.bondingDecisionBasis.join("<br /><br />")
            : "";
        if (summary.requiredCumulativeFinancialInstruments) {
            const lowestOption = this.financialAssuranceService.getLowestCumulativeBondRequirement(<FinancialAssuranceSummary>{ requiredCumulativeFinancialInstruments: summary.requiredCumulativeFinancialInstruments });
            const otherOptions: RequiredCumulativeFinancialInstrument[] = summary.requiredCumulativeFinancialInstruments.filter(x => x != lowestOption);
            if (otherOptions.length) {
                info += `<br /><br />In addition to the minimum amount featured here, you have the following options for satisfying your bonding requirements: `;
                otherOptions.forEach(option => {
                    if (option.typeCode === FinancialInstrumentType.FinancialStatement)
                        info += `<br/><br/>If you have an existing active Financial Statement the required updated financial statement net worth must be greater or equal to  ${this.currencyPipe.transform(option.amount)}`;
                    else
                        info += `<br/><br/>${this.currencyPipe.transform(option.amount)} - ${this.getInstrumentTypeDescription(option.typeCode)}`;
                });
            }
        }
        return info;
    }

    public getInstrumentTypeDescription(typeCode: string): string {
        let match = this.typeDescriptions.find(x => x.id.toLowerCase() === typeCode.toLowerCase());
        if (match != null) return match.description;
        return typeCode;
    }

    public isBondActive(data: FinancialInstrument): boolean {
        return data.isActive;
    }

    public resizeColumns(): void {
        setTimeout(() => this.gridApi?.sizeColumnsToFit(), 0);
    }

    public assuranceType() {
        return (obj) => {
            let value = this.getInstrumentTypeDescription(obj.data.typeCode);
            return value;
        }
    }
    public guarantorNameFormatter(name: string) {
        if (name == null) return "N/A";
        return name;
    }
    public onDialogClose(): void {
    }

    recalculate(): void {
        this.bondingInfo = "";
        this.recalculateEvent.emit(this.orgId);
    }
}
